import { useEffect, useState } from "react";
import { api, resolveImg, espnImg } from "@/lib/api";

const TITANS_LOGO = "https://a.espncdn.com/i/teamlogos/nfl/500/ten.png";

let _roster = null;
let _rosterP = null;
const _esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function findPlayer(list, text) {
  if (!text || !list?.length) return null;
  for (const p of list) if (p.fullName && p.headshot && text.includes(p.fullName)) return p;
  for (const p of list) {
    const ln = (p.lastName || "").trim();
    if (ln.length >= 4 && p.headshot && new RegExp(`\\b${_esc(ln)}\\b`).test(text)) return p;
  }
  return null;
}

function usePlayerCutout(article, enabled) {
  const [player, setPlayer] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    const text = `${article?.headline || ""} ${article?.description || ""}`;
    let dead = false;
    if (_roster) { setPlayer(findPlayer(_roster, text)); return; }
    _rosterP = _rosterP || api.get("/roster")
      .then((r) => { _roster = r.data.athletes || []; return _roster; })
      .catch(() => { _roster = []; return _roster; });
    _rosterP.then((list) => { if (!dead) setPlayer(findPlayer(list, text)); });
    return () => { dead = true; };
  }, [article, enabled]);
  return player;
}

const PALETTES = [
  { match: ["breaking"], bg: "linear-gradient(135deg,#1A050A 0%,#5C0A18 45%,#C8102E 100%)", word: "BREAKING", accent: "#FFD7DE" },
  { match: ["injur"], bg: "linear-gradient(135deg,#160408 0%,#4A0D18 55%,#8F1024 100%)", word: "INJURY WATCH", accent: "#F6B8C1" },
  { match: ["analysis", "film", "opinion", "column"], bg: "linear-gradient(135deg,#05101F 0%,#0C2340 50%,#17457C 100%)", word: "ANALYSIS", accent: "#4B92DB" },
  { match: ["rumor"], bg: "linear-gradient(135deg,#081729 0%,#12314F 55%,#1F5288 100%)", word: "RUMOR MILL", accent: "#7FB4E8" },
  { match: ["editorial"], bg: "linear-gradient(135deg,#0A0F16 0%,#1B2733 55%,#2E3944 100%)", word: "EDITORIAL", accent: "#A2AAAD" },
  { match: ["camp", "practice"], bg: "linear-gradient(135deg,#05101F 0%,#0C2340 55%,#2A5F9E 100%)", word: "CAMP WIRE", accent: "#4B92DB" },
  { match: ["draft"], bg: "linear-gradient(135deg,#0B1220 0%,#14304E 55%,#3B71AE 100%)", word: "DRAFT", accent: "#8FC1F0" },
];
const DEFAULT_PALETTE = { bg: "linear-gradient(135deg,#05101F 0%,#0C2340 55%,#123152 100%)", word: "TITANS", accent: "#4B92DB" };

function pickPalette(article) {
  const raw = `${article?.category || ""} ${article?.subcategory || ""}`.toLowerCase();
  return PALETTES.find((p) => p.match.some((m) => raw.includes(m))) || DEFAULT_PALETTE;
}

/**
 * ArticleThumb — renders the article image when one exists; otherwise a
 * branded editorial graphic (category gradient + word art + logo cutout)
 * instead of a generic stock press photo. Fills its relative parent.
 *
 * `priority=true` marks this thumbnail as the LCP candidate (hero) — it will
 * load eagerly, use fetchpriority=high so the browser prioritises it over
 * lazy-loaded assets, and skips decode-async so the pixels are on-screen
 * as soon as bytes arrive.
 */
export default function ArticleThumb({ article, className = "", size = "md", withHeadline = false, quiet = false, priority = false }) {
  const src = resolveImg(article?.image);
  const player = usePlayerCutout(article, !src);
  // Subtle brand watermark shown on real photos — makes shared images
  // recognizable when someone screenshots without a caption. Skipped on
  // the branded fallback graphics since those already carry the wordmark.
  const Watermark = () => (
    <div
      aria-hidden="true"
      data-testid="article-thumb-watermark"
      className="absolute bottom-2 right-3 select-none pointer-events-none flex items-center gap-1"
      style={{ mixBlendMode: "screen", opacity: 0.55 }}
    >
      <span className="font-cond uppercase tracking-[0.25em] text-[9px] text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
        The Titan Standard
      </span>
    </div>
  );
  if (src) {
    return (
      <>
        <img
          src={src}
          alt={article?.headline || ""}
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.06] ${className}`}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchPriority={priority ? "high" : "auto"}
          width="1600"
          height="900"
        />
        <Watermark />
      </>
    );
  }
  const p = pickPalette(article);
  const sm = size === "sm";
  return (
    <div
      className={`absolute inset-0 overflow-hidden select-none ${className}`}
      style={{ background: p.bg }}
      data-testid="article-thumb-graphic"
      aria-label={article?.headline || ""}
      role="img"
    >
      <div
        className="absolute inset-0 opacity-[0.16]"
        style={{ background: "repeating-linear-gradient(115deg, rgba(255,255,255,0.10) 0 2px, transparent 2px 14px)" }}
      />
      {/* player cutout when the story mentions a Titan; logo watermark otherwise */}
      {player?.headshot ? (
        <div
          aria-hidden="true"
          className={`absolute pointer-events-none aspect-square transition-transform duration-[600ms] group-hover:scale-[1.03] ${sm ? "h-[92%] right-0 bottom-0" : "h-[94%] right-0 md:right-6 bottom-0"}`}
        >
          <img
            src={priority ? player.headshot : espnImg(player.headshot, sm ? 260 : 320)}
            alt=""
            data-testid="article-thumb-cutout"
            className="w-full h-full object-contain object-bottom"
            style={{ filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.55)) saturate(1.05)" }}
            loading={priority ? "eager" : "lazy"}
            decoding={priority ? "sync" : "async"}
            fetchPriority={priority ? "high" : "auto"}
            width="520"
            height="520"
          />
        </div>
      ) : (
      <img
        src={TITANS_LOGO}
        alt=""
        aria-hidden="true"
        className={`absolute pointer-events-none transition-transform duration-[600ms] group-hover:scale-105 ${sm ? "w-24 -right-6 -bottom-6" : "w-52 md:w-72 -right-10 -bottom-12"}`}
        style={{ opacity: quiet ? 0.22 : 0.14, filter: "grayscale(35%)" }}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        width="500"
        height="500"
      />
      )}
      {/* glow accent */}
      <div className="absolute -left-10 -top-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: p.accent, opacity: 0.14, filter: "blur(48px)" }} />
      {!quiet && (
      <div className={`absolute inset-0 flex flex-col justify-end ${sm ? "p-2.5" : "p-4 md:p-5"}`}>
        <div className="flex items-center gap-1.5 mb-1">
          <span className={`block ${sm ? "w-4" : "w-7"} h-[3px]`} style={{ background: p.accent }} />
          <span className={`font-cond uppercase tracking-[0.28em] ${sm ? "text-[7px]" : "text-[9px]"}`} style={{ color: p.accent }}>
            The Titan Standard
          </span>
        </div>
        <div
          className={`font-display uppercase leading-[0.92] ${sm ? "text-lg" : "text-3xl md:text-5xl"}`}
          style={{ color: "#FFFFFF", textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
        >
          {withHeadline && article?.headline ? (
            <span className={sm ? "line-clamp-3 text-base" : "line-clamp-3 text-2xl md:text-4xl"}>{article.headline}</span>
          ) : (
            p.word
          )}
        </div>
      </div>
      )}
    </div>
  );
}
